import React, { useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { PapiDeviation, PapiAttachmentFile } from '@wix/da-papi-types';

import AttachmentCaptionTipTapEditor from '@wix/da-editor-tiptap/pkg/components/Editor/presets/AttachmentCaption';
import { EditorApi } from '@wix/da-editor-tiptap/pkg/components/Editor/types';
import { EditorContextProviderWrapper } from '@wix/da-editor-tiptap/pkg/components/Editor/Context';

import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import useEditorNg from '@wix/da-shared-react/pkg/DaEditorNg/hooks/useEditorNg';
import EditorCommon from '@wix/da-shared-react/pkg/DaEditorNg/EditorCommon';
import {
  setAttachmentCaption,
  setMainDeviationCaption,
} from '@wix/da-shared-react/pkg/redux/uploads/actions';

import { MAX_CAPTION_LENGTH } from '../../../constants';

export type EditorTiptapContentReadyHandler = (api: EditorApi) => void;

export interface Props {
  className?: string;
  deviation: PapiDeviation;
  attachment?: PapiAttachmentFile;
  placeholder?: string;
  autoFocus?: boolean;
  onInitialContentReady?: EditorTiptapContentReadyHandler;
  onMouseUp?: (e) => void;
  onBlur?: (e) => void;
}

const AttachmentCaptionEditor: React.FC<Props> = ({
  className,
  deviation,
  attachment,
  placeholder,
  autoFocus,
  onInitialContentReady,
  onMouseUp,
  onBlur,
}) => {
  const initialContent = attachment?.caption || deviation?.extended?.caption;
  const editorApi = useRef<EditorApi | undefined>(undefined);
  const dispatch = useDispatch();

  const saveCaption = useCallback(() => {
    if (!deviation) {
      return;
    }
    const editorState = editorApi.current?.getStateForSave();
    if (!editorState) {
      return;
    }

    if (attachment?.fileId) {
      dispatch(
        setAttachmentCaption(
          attachment.fileId!,
          deviation.deviationId,
          attachment.type!,
          editorState
        )
      );
    } else {
      dispatch(setMainDeviationCaption(deviation.deviationId, editorState));
    }
  }, [dispatch, attachment, deviation]);

  const handleOnBlur = useCallback(
    params => {
      saveCaption();
      onBlur?.(params.event);
    },
    [saveCaption, onBlur]
  );

  const handleOnEditorReady = useCallback(
    (api: EditorApi) => {
      onInitialContentReady && onInitialContentReady(api);
      editorApi.current = api;
      if (autoFocus) {
        api.focusEditor();
      }
    },
    [autoFocus, onInitialContentReady]
  );

  const { editorProps, editorSharedProps, editorCommonProps } = useEditorNg({
    onBlur: handleOnBlur,
    onEditorReady: handleOnEditorReady,
  });

  return (
    <div className={className} onMouseUp={onMouseUp}>
      <EditorContextProviderWrapper>
        <EditorCommon {...editorCommonProps} />
        <AttachmentCaptionTipTapEditor
          cookieType={MeasuredCookieType.ATTACHMENT_CAPTION}
          {...editorSharedProps}
          {...editorProps}
          placeholder={placeholder}
          initialContent={initialContent}
          characterCountOptions={
            {
              limit: MAX_CAPTION_LENGTH,
              mode: 'textSize',
            } as any
          }
        ></AttachmentCaptionTipTapEditor>
      </EditorContextProviderWrapper>
    </div>
  );
};
AttachmentCaptionEditor.displayName = 'AttachmentCaptionEditor';

export default AttachmentCaptionEditor;
